/**
 * 1.3.0 Administration → Media portal → Upload rules (P1): who may upload without a placeholder (open uploads), per
 * company or per user (a user's rule wins over the company's): allowed or not, types and size, an optional QC profile,
 * where an accepted upload goes (collection, asset class, default metadata), what the supplier fills in, acceptance
 * and the reviewer.
 */
import { useState } from 'react';
import { Plus, Trash2, UploadCloud } from 'lucide-react';
import type { AcceptanceMode, ReviewerRef, UploadRuleDTO } from '@prism/contracts';
import { api, errorDetail } from '../../../api/client';
import { usePortalAdminRefresh, usePortalChoices, usePortalCompanies, usePortalUsers, useUploadRules } from '../../../api/hooks/portalAdmin';
import { Modal } from '../../../components/Modal';
import { TableScroll } from '../../../components/TableScroll';
import { toast } from '../../../components/Toasts';
import { ACCEPTANCE_LABEL, AcceptanceOptions, FieldPicker, ReviewerSelect, TypesInput, fromGib, gibLabel, reviewerLabel, toGib } from './shared';

export function PortalRules() {
  const rules = useUploadRules();
  const choices = usePortalChoices().data;
  const refresh = usePortalAdminRefresh();
  const [editing, setEditing] = useState<UploadRuleDTO | 'new' | null>(null);
  const items = rules.data?.items ?? [];
  const name = (list: Array<{ id: string; name: string }> | undefined, id: string | null) => (id ? list?.find((x) => x.id === id)?.name ?? '—' : '—');
  const remove = async (r: UploadRuleDTO) => {
    if (!window.confirm(`Delete the upload rule of ${r.subjectName}? They can no longer upload without an assignment.`)) return;
    try {
      await api(`/api/v1/admin/portal/rules/${r.id}`, { method: 'DELETE' });
      await refresh();
      toast('Upload rule deleted');
    } catch (err) {
      toast(errorDetail(err), 'error');
    }
  };
  return (
    <section className="panel-card" data-testid="portal-rules">
      <div className="portal-card-head">
        <h3><UploadCloud size={16} color="var(--accent-strong)" /> Upload rules</h3>
        <button className="btn sm" onClick={() => setEditing('new')} data-testid="portal-add-rule"><Plus size={14} /> New rule</button>
      </div>
      <p className="muted portal-lede">Open uploads: chosen companies or users may upload clips and footage without a placeholder. Each upload waits in Intake (or is accepted when QC passes) before it reaches the Library. A user’s own rule wins over their company’s.</p>
      <TableScroll>
        <table className="table compact">
          <thead><tr><th>For</th><th>Uploads</th><th>Files</th><th>Destination</th><th>Acceptance</th><th>Reviewer</th><th /></tr></thead>
          <tbody>
            {rules.data && !items.length && <tr><td colSpan={7} className="muted">No upload rules: suppliers deliver into assigned placeholders only.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className={r.enabled ? undefined : 'faint'} data-rule={r.subjectName}>
                <td className="wrap" style={{ minWidth: 150 }}><div style={{ fontWeight: 560 }}>{r.subjectName}</div><div className="faint">{r.portalUserId ? 'User' : 'Company'}</div></td>
                <td className="nowrap">{r.enabled ? <span className="pill accent">Allowed</span> : <span className="pill">Not allowed</span>}</td>
                <td className="wrap" style={{ minWidth: 110 }}>{r.acceptedTypes.length ? r.acceptedTypes.join(', ') : 'Any type'}<div className="faint">{gibLabel(r.maxBytes)}</div></td>
                <td className="wrap" style={{ minWidth: 140 }}>{name(choices?.collections, r.collectionId)}<div className="faint">{name(choices?.categories, r.categoryId)}</div></td>
                <td className="wrap">{ACCEPTANCE_LABEL[r.acceptance]}</td>
                <td className="wrap">{reviewerLabel(r.reviewer)}</td>
                <td className="actions"><div>
                  <button className="btn ghost sm" onClick={() => setEditing(r)} data-testid="portal-edit-rule">Edit</button>
                  <button className="btn ghost sm icon" aria-label={`Delete the rule of ${r.subjectName}`} onClick={() => void remove(r)}><Trash2 size={14} /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
      {editing && <RuleModal rule={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </section>
  );
}

function RuleModal({ rule, onClose }: { rule: UploadRuleDTO | null; onClose: () => void }) {
  const choices = usePortalChoices().data;
  const companies = usePortalCompanies().data?.items ?? [];
  const refresh = usePortalAdminRefresh();
  const [subject, setSubject] = useState<'company' | 'user'>(rule?.portalUserId ? 'user' : 'company');
  const [companyId, setCompanyId] = useState(rule?.companyId ?? '');
  const [userCompanyId, setUserCompanyId] = useState('');
  const [portalUserId, setPortalUserId] = useState(rule?.portalUserId ?? '');
  const users = usePortalUsers(subject === 'user' && !rule ? userCompanyId || null : null).data?.items ?? [];
  const [v, setV] = useState({
    enabled: rule?.enabled ?? true, acceptedTypes: rule?.acceptedTypes ?? [], maxGib: toGib(rule?.maxBytes), qcProfileId: rule?.qcProfileId ?? '',
    collectionId: rule?.collectionId ?? '', categoryId: rule?.categoryId ?? '', defaultMetadata: (rule?.defaultMetadata ?? {}) as Record<string, unknown>,
    supplierFields: rule?.supplierFields ?? [], requiredFields: rule?.requiredFields ?? [], acceptance: rule?.acceptance ?? 'review' as AcceptanceMode,
    groupFiles: rule?.groupFiles ?? false, reviewer: (rule?.reviewer ?? null) as ReviewerRef | null,
  });
  const [defaultField, setDefaultField] = useState('');
  const [busy, setBusy] = useState(false);
  const fields = choices?.fields ?? [];
  async function save() {
    setBusy(true);
    try {
      const body = {
        enabled: v.enabled, acceptedTypes: v.acceptedTypes, maxBytes: fromGib(v.maxGib), qcProfileId: v.qcProfileId || null, collectionId: v.collectionId || null,
        categoryId: v.categoryId || null, defaultMetadata: v.defaultMetadata, supplierFields: v.supplierFields, requiredFields: v.requiredFields,
        acceptance: v.acceptance, groupFiles: v.groupFiles, reviewer: v.reviewer,
      };
      if (rule) await api(`/api/v1/admin/portal/rules/${rule.id}`, { method: 'PATCH', json: body });
      else await api('/api/v1/admin/portal/rules', { method: 'POST', json: { ...body, companyId: subject === 'company' ? companyId : null, portalUserId: subject === 'user' ? portalUserId : null } });
      await refresh();
      toast('Upload rule saved');
      onClose();
    } catch (err) {
      toast(errorDetail(err), 'error');
      setBusy(false);
    }
  }
  const subjectChosen = rule || (subject === 'company' ? companyId : portalUserId);
  return (
    <Modal wide title={rule ? `Upload rule of ${rule.subjectName}` : 'New upload rule'} onClose={onClose} actions={<>
      <button className="btn" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={busy || !subjectChosen} onClick={() => void save()} data-testid="portal-rule-save">{rule ? 'Save' : 'Add rule'}</button>
    </>}>
      {!rule && (
        <div className="field-row">
          <div className="field">
            <label htmlFor="ur-subject">For</label>
            <select id="ur-subject" className="select" value={subject} onChange={(e) => setSubject(e.target.value as 'company' | 'user')} data-testid="portal-rule-subject">
              <option value="company">A company (all its users)</option>
              <option value="user">One user</option>
            </select>
          </div>
          {subject === 'company' ? (
            <div className="field">
              <label htmlFor="ur-company">Company</label>
              <select id="ur-company" className="select" value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="portal-rule-company">
                <option value="">Choose…</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          ) : (
            <div className="field">
              <label htmlFor="ur-user">User</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select className="select" aria-label="Their company" value={userCompanyId} onChange={(e) => { setUserCompanyId(e.target.value); setPortalUserId(''); }}>
                  <option value="">Company…</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select id="ur-user" className="select" value={portalUserId} disabled={!userCompanyId} onChange={(e) => setPortalUserId(e.target.value)} data-testid="portal-rule-user">
                  <option value="">User…</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.displayName}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
      )}
      <label className="toggle-row"><input type="checkbox" checked={v.enabled} onChange={(e) => setV({ ...v, enabled: e.target.checked })} data-testid="portal-rule-enabled" /><span>Uploads allowed</span></label>
      <div className="field-row three">
        <div className="field"><label htmlFor="ur-types">Accepted file types</label><TypesInput id="ur-types" value={v.acceptedTypes} onChange={(acceptedTypes) => setV({ ...v, acceptedTypes })} testId="portal-rule-types" /></div>
        <div className="field"><label htmlFor="ur-max">Largest file (GiB)</label><input id="ur-max" className="input" inputMode="decimal" placeholder="Portal maximum" value={v.maxGib} onChange={(e) => setV({ ...v, maxGib: e.target.value })} /></div>
        <div className="field">
          <label htmlFor="ur-qc">QC profile</label>
          <select id="ur-qc" className="select" value={v.qcProfileId} onChange={(e) => setV({ ...v, qcProfileId: e.target.value })}>
            <option value="">No automated QC</option>
            {choices?.qcProfiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <label htmlFor="ur-collection">Collection for accepted uploads</label>
          <select id="ur-collection" className="select" value={v.collectionId} onChange={(e) => setV({ ...v, collectionId: e.target.value })} data-testid="portal-rule-collection">
            <option value="">None</option>
            {choices?.collections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <span className="help">Shared collections only. The reviewer can change it when accepting.</span>
        </div>
        <div className="field">
          <label htmlFor="ur-class">Asset class</label>
          <select id="ur-class" className="select" value={v.categoryId} onChange={(e) => setV({ ...v, categoryId: e.target.value })} data-testid="portal-rule-class">
            <option value="">None</option>
            {choices?.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>
      <div className="field">
        <label>Default metadata</label>
        <span className="help">Set on every accepted upload unless the supplier or the reviewer fills it in.</span>
        {Object.entries(v.defaultMetadata).map(([id, value]) => (
          <div key={id} className="portal-default-row">
            <span className="portal-default-label">{fields.find((f) => f.id === id)?.label ?? id}</span>
            {fields.find((f) => f.id === id)?.options?.length ? (
              <select className="select" aria-label={`Default for ${id}`} value={String(value ?? '')} onChange={(e) => setV({ ...v, defaultMetadata: { ...v.defaultMetadata, [id]: e.target.value } })}>
                <option value="">Choose…</option>
                {fields.find((f) => f.id === id)!.options!.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <input className="input" aria-label={`Default for ${id}`} value={String(value ?? '')} onChange={(e) => setV({ ...v, defaultMetadata: { ...v.defaultMetadata, [id]: e.target.value } })} />
            )}
            <button className="btn ghost sm icon" aria-label="Remove" onClick={() => { const { [id]: _drop, ...rest } = v.defaultMetadata; setV({ ...v, defaultMetadata: rest }); }}><Trash2 size={14} /></button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8 }}>
          <select className="select" aria-label="Field for a default value" value={defaultField} onChange={(e) => setDefaultField(e.target.value)} data-testid="portal-rule-default-field">
            <option value="">Add a default for…</option>
            {fields.filter((f) => !(f.id in v.defaultMetadata) && ['text', 'rich_text', 'vocabulary'].includes(f.type)).map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
          <button className="btn ghost sm" disabled={!defaultField} onClick={() => { setV({ ...v, defaultMetadata: { ...v.defaultMetadata, [defaultField]: '' } }); setDefaultField(''); }}><Plus size={14} /> Add</button>
        </div>
      </div>
      <div className="field">
        <label>Metadata the supplier fills in</label>
        <FieldPicker choices={choices} chosen={v.supplierFields} onChosen={(supplierFields) => setV({ ...v, supplierFields, requiredFields: v.requiredFields.filter((f) => supplierFields.includes(f)) })}
          required={v.requiredFields} onRequired={(requiredFields) => setV({ ...v, requiredFields })} testId="portal-rule-fields" />
      </div>
      <div className="field-row">
        <div className="field">
          <label htmlFor="ur-accept">Acceptance</label>
          <select id="ur-accept" className="select" value={v.acceptance} onChange={(e) => setV({ ...v, acceptance: e.target.value as AcceptanceMode })} data-testid="portal-rule-acceptance">
            <AcceptanceOptions reviewLabel="Reviewed in Intake" />
          </select>
        </div>
        <div className="field">
          <label htmlFor="ur-reviewer">Reviewer</label>
          <ReviewerSelect id="ur-reviewer" value={v.reviewer} onChange={(reviewer) => setV({ ...v, reviewer })} choices={choices} testId="portal-rule-reviewer" />
        </div>
      </div>
      <label className="toggle-row"><input type="checkbox" checked={v.groupFiles} onChange={(e) => setV({ ...v, groupFiles: e.target.checked })} /><span>The files of one upload batch become one submission (otherwise one submission per file)</span></label>
    </Modal>
  );
}
